import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * Facility redirect — an old slug that should send visitors somewhere else.
 *
 * Three kinds, and which fields matter depends on which one:
 *
 *   1 Facility slug rename  → a target slug
 *   2 Product slug rename   → a target slug (source = old product slug, target =
 *                             new one; the resolver ignores ProductSlug)
 *   3 Full URL redirect     → a relative target URL and a product slug, "*" for
 *                             every product (without one it never fires)
 *
 * The old form clears the fields that stop applying when you switch type, and so
 * does this one: leaving a target URL behind on a slug rename means saving a
 * value the backend will ignore, which is how a redirect ends up looking correct
 * and doing nothing.
 *
 * Every action here — save, activate, delete — needs the application restarted
 * before it takes effect. The old page says so inside a `window.confirm`; here it
 * is on the page and in each dialog, because it is the difference between "I
 * changed it and it does not work" and "I changed it and it will".
 *
 * Detail and edit are one page, as with areas and providers.
 */

const TYPES = [
  { value: 1, name: 'FacilitySlugRename', label: 'Facility slug rename', needs: ['TargetSlug'] },
  { value: 2, name: 'ProductSlugRename', label: 'Product slug rename', needs: ['TargetSlug'] },
  { value: 3, name: 'FullUrlRedirect', label: 'Full URL redirect', needs: ['TargetUrl', 'ProductSlug'] }
];

const typeOf = value => TYPES.find(type => type.value === Number(value)) || TYPES[0];

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const FacilityRedirectInternal = ({ api }) => {
  const { redirectId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const isNew = !redirectId;

  const empty = {
    SourceSlug: '', TargetSlug: '', TargetUrl: '', ProductSlug: '', Type: 1, IsActive: true
  };

  const [redirect, setRedirect] = useState(isNew ? {} : null);
  const [form, setForm] = useState(isNew ? empty : null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const toForm = data => ({
    SourceSlug: data.sourceSlug || '',
    TargetSlug: data.targetSlug || '',
    TargetUrl: data.targetUrl || '',
    ProductSlug: data.productSlug || '',
    Type: (TYPES.find(type => type.name === data.type) || TYPES[0]).value,
    IsActive: data.isActive !== false
  });

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await api.facilityRedirects.getFacilityRedirect(redirectId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setRedirect(data);
      setForm(toForm(data));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, redirectId, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/facility-redirects">← All redirects</BackToList>
        <div className="next-note next-note--error">
          Could not load this redirect.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!form) return <div className="next-page next-muted">Loading…</div>;

  const kind = typeOf(form.Type);

  const set = (name, value) => setForm(previous => {
    const next = { ...previous, [name]: value };

    // Switching type clears what that type does not use, the same way the old
    // form does — a leftover value would be saved and then ignored.
    if (name === 'Type') {
      const needs = typeOf(value).needs;
      if (!needs.includes('TargetSlug')) next.TargetSlug = '';
      if (!needs.includes('TargetUrl')) next.TargetUrl = '';
      // Only type 3 reads ProductSlug, and it needs one: "*" unless a product is named.
      if (!needs.includes('ProductSlug')) next.ProductSlug = '';
      else if (!next.ProductSlug.trim()) next.ProductSlug = '*';
    }

    return next;
  });

  const pristine = isNew ? empty : toForm(redirect);
  const dirty = Object.keys(form).some(key => form[key] !== pristine[key]);

  const errors = {
    SourceSlug: form.SourceSlug.trim() ? null : 'A redirect needs a source slug.',
    TargetSlug: kind.needs.includes('TargetSlug') && !form.TargetSlug.trim()
      ? 'This type redirects to a slug.' : null,
    TargetUrl: !kind.needs.includes('TargetUrl') ? null
      : !form.TargetUrl.trim() ? 'This type redirects to a URL.'
        : (form.TargetUrl.includes('://') || form.TargetUrl.trim().startsWith('/'))
          ? 'A path on the site, without domain and without a leading "/".' : null,
    ProductSlug: kind.needs.includes('ProductSlug') && !form.ProductSlug.trim()
      ? 'Use "*" for every product, or a product slug.' : null
  };
  const invalid = Object.values(errors).some(Boolean);

  const save = async event => {
    event.preventDefault();
    if (invalid || (!dirty && !isNew) || busy) return;

    const payload = {
      SourceSlug: form.SourceSlug.trim(),
      TargetSlug: form.TargetSlug.trim() || null,
      TargetUrl: form.TargetUrl.trim() || null,
      Type: Number(form.Type),
      ProductSlug: form.ProductSlug.trim() || null,
      IsActive: form.IsActive
    };

    setBusy(true);
    setNote(null);
    try {
      const result = isNew
        ? await api.facilityRedirects.createFacilityRedirect(payload)
        : await api.facilityRedirects.updateFacilityRedirect({ ...payload, Id: redirectId });

      if (result?.success === false) {
        setNote({ tone: 'error', text: result.errorMessage || 'The redirect was not saved.' });
        return;
      }

      if (isNew && result?.id) {
        navigate(`/next/facility-redirect/${result.id}`);
        return;
      }
      await load();
      setNote({ tone: 'info', text: 'Saved. Restart the application for it to take effect.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.payload?.translatedMessage || error?.payload?.message
          || error?.response?.data?.message || error?.message || 'Unable to save the redirect.'
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = () => ask({
    kind: 'confirm',
    title: form.IsActive ? 'Deactivate this redirect?' : 'Activate this redirect?',
    context: `${form.IsActive ? 'Deactivate' : 'Activate'} · ${form.SourceSlug}`,
    body: form.IsActive
      ? `Visitors on /${form.SourceSlug} stop being redirected and get whatever that address serves on its own.`
      : `Visitors on /${form.SourceSlug} start being sent to ${form.TargetSlug || form.TargetUrl || 'the target'}.`,
    warning: 'The application has to be restarted before this takes effect.',
    confirmLabel: form.IsActive ? 'Deactivate' : 'Activate',
    onConfirm: async () => {
      try {
        await api.facilityRedirects.toggleActiveFacilityRedirect(redirectId);
      } catch (error) {
        setNote({ tone: 'error', text: error?.message || 'Unable to change it.' });
        throw error;
      }
      await load();
      setNote({ tone: 'info', text: 'Changed. Restart the application for it to take effect.' });
    }
  });

  const remove = () => ask({
    kind: 'danger',
    title: 'Delete this redirect?',
    context: `Delete · ${form.SourceSlug}`,
    body: `/${form.SourceSlug} stops redirecting anywhere. Links and search results pointing at it will break rather than land on ${form.TargetSlug || form.TargetUrl || 'the target'}.`,
    warning: 'This cannot be undone, and the application has to be restarted afterwards.',
    confirmLabel: 'Delete redirect',
    onConfirm: async () => {
      try {
        await api.facilityRedirects.deleteFacilityRedirect(redirectId);
      } catch (error) {
        setNote({
          tone: 'error',
          text: error?.response?.data?.message || error?.message || 'Unable to delete the redirect.'
        });
        throw error;
      }
      navigate('/next/facility-redirects');
    }
  });

  const field = (name, label, { hint, placeholder } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        value={form[name]}
        placeholder={placeholder}
        onChange={event => set(name, event.target.value)}
      />
      {(errors[name] || hint) && (
        <span className={errors[name] ? 'next-form__error' : 'next-form__hint'}>
          {errors[name] || hint}
        </span>
      )}
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/facility-redirects">← All redirects</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{isNew ? 'New redirect' : `/${form.SourceSlug}`}</h1>
          {!isNew && (
            <span className={`next-pill next-pill--${form.IsActive ? 'ok' : 'off'}`}>
              {form.IsActive ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
        {!isNew && <span className="next-detail__id">{redirect.id}</span>}
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-note next-note--warn">
        Redirects are read at start-up: the application has to be restarted before
        anything on this page changes what visitors get.
      </div>

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head"><h2>Redirect</h2></div>
              <div className="next-form__grid">
                {field('SourceSlug', 'Source slug', { hint: 'The address people arrive on.', placeholder: 'old-venue-name' })}

                <label className="next-filter">
                  <span className="next-filter__label">Type</span>
                  <select
                    className="next-input"
                    value={form.Type}
                    onChange={event => set('Type', Number(event.target.value))}
                  >
                    {TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </label>

                {kind.needs.includes('TargetSlug')
                  && field('TargetSlug', 'Target slug', { placeholder: 'new-venue-name' })}

                {kind.needs.includes('TargetUrl')
                  && field('TargetUrl', 'Target URL', { placeholder: 'copenhagen/padel' })}

                {/* Only a full URL redirect reads it, and needs it: the backend
                    resolver never fires one without a product slug. */}
                {kind.needs.includes('ProductSlug') && field('ProductSlug', 'Product slug', {
                  hint: '"*" redirects every product of the facility; a slug only that product.'
                })}

                <label className="next-filter next-filter--check">
                  <input
                    type="checkbox"
                    checked={form.IsActive}
                    onChange={event => set('IsActive', event.target.checked)}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || (!dirty && !isNew) || busy}
                  title={invalid ? 'Some fields need fixing first' : (!dirty && !isNew ? 'Nothing has changed' : undefined)}
                >
                  {busy ? 'Saving…' : (isNew ? 'Create redirect' : 'Save changes')}
                </button>
                {!isNew && (
                  <button
                    type="button"
                    className="next-btn"
                    disabled={!dirty || busy}
                    onClick={() => setForm(toForm(redirect))}
                  >
                    Discard
                  </button>
                )}
                {dirty && !isNew && <span className="next-muted">Unsaved changes</span>}
              </div>
            </section>

            {!isNew && (
              <section className="next-card">
                <div className="next-card__head"><h2>Read only</h2></div>
                <div className="next-facts">
                  <div className="next-fact">
                    <span className="next-fact__label">Id</span>
                    <span className="next-fact__value"><Guid value={redirect.id} /></span>
                  </div>
                  <div className="next-fact">
                    <span className="next-fact__label">Created at</span>
                    <span className="next-fact__value">{fmtDate(redirect.createdAt)}</span>
                  </div>
                  <div className="next-fact">
                    <span className="next-fact__label">Updated at</span>
                    <span className="next-fact__value">{fmtDate(redirect.updatedAt)}</span>
                  </div>
                </div>
              </section>
            )}
          </form>
        </div>

        {!isNew && (
          <aside className="next-detail__side">
            <section className="next-card">
              <div className="next-card__head"><h2>Actions</h2></div>
              <div className="next-actionlist">
                <button type="button" className="next-actionlist__item" onClick={toggleActive}>
                  {form.IsActive ? 'Deactivate redirect' : 'Activate redirect'}
                </button>
                <button
                  type="button"
                  className="next-actionlist__item next-actionlist__item--danger"
                  onClick={remove}
                >
                  Delete redirect
                </button>
              </div>
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>Links</h2></div>
              <RelatedLinks
                items={[
                  form.TargetSlug && {
                    label: 'Target facility',
                    hint: form.TargetSlug,
                    to: `/next/facilities?name=${encodeURIComponent(form.TargetSlug)}`
                  },
                  form.SourceSlug && {
                    label: 'Other redirects here',
                    hint: 'same target',
                    to: `/next/facility-redirects?targetSlug=${encodeURIComponent(form.TargetSlug || '')}`
                  },
                  form.TargetUrl && { label: 'Open the target URL', href: form.TargetUrl }
                ]}
              />
            </section>
          </aside>
        )}
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

FacilityRedirectInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const FacilityRedirect = withApi(FacilityRedirectInternal);

FacilityRedirect.requiresLogin = true;

export default FacilityRedirect;
